import React, { useState, useEffect } from 'react';

function InvestmentModal({ startup, maxAmount, onClose, onInvest, draftInvestment }) {
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');

  // Load draft investment if available
  useEffect(() => {
    if (draftInvestment) {
      setAmount(draftInvestment.amount.toString());
    } else {
      setAmount('');
    }
  }, [draftInvestment]);

  const handleAmountChange = (e) => {
    const value = e.target.value;
    
    // Allow empty input
    if (value === '') {
      setAmount('');
      setError('');
      return;
    }

    // Only allow numbers (both . and , as decimal separator)
    if (!/^\d*[,.]?\d*$/.test(value)) {
      return;
    }

    // Convert comma to dot for parsing
    const normalizedValue = value.replace(',', '.');
    const investmentAmount = parseFloat(normalizedValue);
    
    // Allow 0, but check if amount exceeds budget (only if > 0)
    if (!isNaN(investmentAmount) && investmentAmount > 0 && investmentAmount > maxAmount) {
      setError(`Das Budget darf ${maxAmount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })} nicht überschreiten`);
    } else if (!isNaN(investmentAmount) && investmentAmount < 0) {
      setError('Der Betrag darf nicht negativ sein');
    } else {
      setError('');
    }

    setAmount(value);
  };

  const handleClear = () => {
    setAmount('');
    setError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Allow empty amount (clears draft)
    if (amount === '') {
      onInvest(0);
      return;
    }

    // Convert comma to dot for parsing
    const normalizedValue = amount.replace(',', '.');
    const investmentAmount = parseFloat(normalizedValue);

    // Allow 0 explicitly (clears draft)
    if (isNaN(investmentAmount) || investmentAmount < 0) {
      setError('Bitte geben Sie einen gültigen Betrag ein');
      return;
    }

    if (investmentAmount === 0) {
      onInvest(0);
      return;
    }

    if (investmentAmount > maxAmount) {
      setError(`Das Budget darf ${maxAmount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })} nicht überschreiten`);
      return;
    }

    onInvest(investmentAmount);
  };

  const quickAmounts = [
    maxAmount * 0.1,
    maxAmount * 0.25,
    maxAmount * 0.5,
    maxAmount
  ].filter(amt => amt <= maxAmount).map(amt => Math.floor(amt / 1000) * 1000);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="card max-w-md w-full bg-white" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Invest in {startup.name}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Investment Amount
            </label>
            <div className="relative">
              <input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={handleAmountChange}
                max={maxAmount}
                placeholder="0"
                className={`input-field text-lg ${error ? 'border-red-500' : ''}`}
              />
              {amount && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Betrag löschen"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            <div className="text-sm text-gray-500 mt-1">
              Maximal: {maxAmount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
            </div>
          </div>

          {quickAmounts.length > 0 && (
            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">Quick Amounts</div>
              <div className="grid grid-cols-2 gap-2">
                {quickAmounts.map((quickAmount) => (
                  <button
                    key={quickAmount}
                    type="button"
                    onClick={() => {
                      setAmount(quickAmount.toString());
                      setError('');
                    }}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
                  >
                    {quickAmount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
                  </button>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 btn-primary"
            >
              Invest
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default InvestmentModal;
